import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle,
  Send,
  MessageSquare,
} from "lucide-react";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "info@momentumva.com.au",
      href: "mailto:info@momentumva.com.au",
    },
    {
      icon: MapPin,
      label: "Service Area",
      value: "Australia and New Zealand-wide",
      href: null,
    },
    {
      icon: Clock,
      label: "Response Time",
      value: "Within 1 business day",
      href: null,
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="font-['Plus_Jakarta_Sans']" data-testid="contact-page">
        <section className="py-32 bg-slate-50">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-20 h-20 rounded-full bg-[#2A9D8F] flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mb-4 font-['Outfit']">
              Message Received!
            </h1>
            <p className="text-slate-600 text-lg mb-8">
              Thank you for reaching out. We'll get back to you within 1 business 
              day to discuss how we can support your real estate office.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: "",
                  email: "",
                  phone: "",
                  company: "",
                  message: "",
                });
              }}
              className="bg-[#0B1C3E] hover:bg-[#0B1C3E]/90 text-white rounded-full px-6"
              data-testid="send-another-btn"
            >
              Send Another Message
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="font-['Plus_Jakarta_Sans']" data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with Momentum VA — book a free discovery call to discuss how our virtual assistants can support your Australian real estate office."
        path="/contact"
      />
      <section className="relative bg-[#0B1C3E] py-20 lg:py-28" data-testid="contact-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/3 w-72 h-72 bg-[#2A9D8F] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/3 w-72 h-72 bg-[#5D6D35] rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Get In Touch
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6 font-['Outfit']">
              Let's Start a Conversation
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Ready to streamline your real estate operations? Book a free discovery 
              call or send us a message. We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#0B1C3E] font-['Outfit']">
                  Contact Information
                </h2>
                <p className="text-slate-600 mt-4">
                  Reach out anytime. We'll respond within 1 business day to schedule 
                  your free discovery call.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#0B1C3E] flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">{info.label}</p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-[#0B1C3E] font-medium hover:text-[#2A9D8F] transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-[#0B1C3E] font-medium">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Image */}
              <div className="rounded-2xl overflow-hidden shadow-lg mt-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_momentum-admin/artifacts/7xvxfx3o_DSC06036.jpg"
                  alt="Professional meeting"
                  className="w-full h-64 object-cover"
                />
              </div>

              {/* Trust Message */}
              <div className="bg-white rounded-xl p-6 border border-slate-100">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-[#2A9D8F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0B1C3E] mb-1 font-['Outfit']">
                      Confidential & Professional
                    </h4>
                    <p className="text-slate-600 text-sm">
                      All enquiries are handled with strict confidentiality. 
                      We're here to understand your needs, not pressure you.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-lg border border-slate-100">
                <div className="flex items-center space-x-3 mb-8">
                  <div className="w-10 h-10 rounded-lg bg-[#E86C3F] flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#0B1C3E] font-['Outfit']">
                      Book Your Discovery Call
                    </h3>
                    <p className="text-slate-500 text-sm">No obligation, just a conversation</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  {/* Name & Email */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#0B1C3E] font-medium">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                        placeholder="Your full name"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#0B1C3E] font-medium">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                        placeholder="your@email.com"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>

                  {/* Phone & Company */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-[#0B1C3E] font-medium">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                        placeholder="+61 XXX XXX XXX"
                        data-testid="contact-input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-[#0B1C3E] font-medium">
                        Company Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                        placeholder="Your real estate office"
                        data-testid="contact-input-company"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-[#0B1C3E] font-medium">
                      How can we help? *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="mt-2 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                      placeholder="Tell us about your office and what support you're looking for..."
                      data-testid="contact-input-message"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#E86C3F] hover:bg-[#d65a2e] text-white rounded-full py-6 text-base font-semibold shadow-lg"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>

                  <p className="text-slate-500 text-sm text-center">
                    By submitting this form, you agree to be contacted by Momentum VA 
                    regarding your enquiry.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Quick Section */}
      <section className="py-16 bg-white" data-testid="quick-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl font-semibold text-[#0B1C3E] mb-6 font-['Outfit']">
            What happens after I submit?
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "1", text: "We review your enquiry within 24 hours" },
              { step: "2", text: "We reach out to schedule a discovery call" },
              { step: "3", text: "We discuss your needs and explore solutions" },
            ].map((item, index) => (
              <div key={index} className="flex items-center space-x-3 justify-center">
                <div className="w-8 h-8 rounded-full bg-[#2A9D8F] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">{item.step}</span>
                </div>
                <p className="text-slate-600 text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
